/**
 * The audio scheduler (architecture checklist 4; art §8.2.3; ui-ux §8.3). Sounds are procedural WebAudio placeholders
 * until real assets exist; buses `master → { music, sfx, ui }`.
 *
 * **Timing:** `play(id, { atMs })` schedules a sound at a PRESENTATION instant (e.g. `BeatPlan.contactMs`): the
 * scheduler maps `presentMs` to `AudioContext.currentTime` through the instant last shown and the clock scale, so a
 * contact SFX lands on the frame its contact is drawn. Voices ≤ 12, ≤ 3 per id, ±4 % pitch on repeats; nothing plays
 * while the document is hidden; the context is created on the first user gesture (autoplay rules).
 */
import type { Clock } from '../shared';

export type SoundId = 'ui_tap' | 'ui_select' | 'ui_page' | 'ui_sheet' | 'ui_deny' | 'ui_seal' | 'coin' | 'xp_tick' | 'levelup'
  | 'hit_light' | 'hit_heavy' | 'crit' | 'ko' | 'omen_open' | 'windup' | 'prayer' | 'card_draw' | 'card_place' | 'quest_done'
  | 'notify' | 'error';
type Bus = 'music' | 'sfx' | 'ui';

const BUS_OF: Record<SoundId, Bus> = {
  ui_tap: 'ui', ui_select: 'ui', ui_page: 'ui', ui_sheet: 'ui', ui_deny: 'ui', ui_seal: 'ui', notify: 'ui', error: 'ui',
  coin: 'sfx', xp_tick: 'sfx', levelup: 'sfx', hit_light: 'sfx', hit_heavy: 'sfx', crit: 'sfx', ko: 'sfx', omen_open: 'sfx',
  windup: 'sfx', prayer: 'sfx', card_draw: 'sfx', card_place: 'sfx', quest_done: 'sfx',
};
export const MAX_VOICES = 12;
export const MAX_SAME_ID = 3;

export interface AudioLogEntry { id: SoundId; atMs: number; ctxTime: number | null }

export class AudioScheduler {
  private ctx: AudioContext | null = null;
  private buses: Record<'master' | Bus, GainNode> | null = null;
  private readonly active: Array<{ id: SoundId; end: number }> = [];
  private readonly repeats = new Map<SoundId, number>();
  private shown = { presentMs: 0, perfMs: 0 };
  private muted = false;
  private volumes = { music: 0.5, sfx: 0.8 };
  /** The last 200 scheduled sounds (`__marg.render.audioLog`, ui-ux `__marg.ui.audio.log`). */
  readonly log: AudioLogEntry[] = [];

  constructor(private readonly clock: Clock) {}

  /** Creates the AudioContext; call from a user gesture. Safe to call repeatedly. */
  unlock(): void {
    if (this.ctx || typeof AudioContext === 'undefined') { void this.ctx?.resume(); return; }
    try {
      const ctx = new AudioContext();
      const master = ctx.createGain();
      master.connect(ctx.destination);
      const mk = (g: number): GainNode => { const n = ctx.createGain(); n.gain.value = g; n.connect(master); return n; };
      this.buses = { master, music: mk(this.volumes.music), sfx: mk(this.volumes.sfx), ui: mk(this.volumes.sfx) };
      master.gain.value = this.muted ? 0 : 1;
      this.ctx = ctx;
    } catch (e) {
      console.warn('[audio] AudioContext unavailable', e);
    }
  }

  setVolumes(v: { music: number; sfx: number }): void {
    this.volumes = v;
    if (this.buses) { this.buses.music.gain.value = v.music; this.buses.sfx.gain.value = v.sfx; this.buses.ui.gain.value = v.sfx; }
  }

  /** `M` toggles mute (ui-ux §7.5; reserved key). */
  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.buses) this.buses.master.gain.value = this.muted ? 0 : 1;
    return this.muted;
  }
  get isMuted(): boolean { return this.muted; }

  /** The loop reports each presented instant (and its wall-ish time) so presentation ms map to audio time. */
  markFrame(presentMs: number): void { this.shown = { presentMs, perfMs: typeof performance !== 'undefined' ? performance.now() : 0 }; }

  /** Schedules a placeholder sound at presentation instant `atMs` (default: now). */
  play(id: SoundId, opts: { atMs?: number; gain?: number } = {}): void {
    const atMs = opts.atMs ?? this.clock.presentMs;
    let when: number | null = null;
    if (this.ctx && this.buses && !(typeof document !== 'undefined' && document.hidden)) {
      const scale = this.clock.paused ? 0 : this.clock.timeScale;
      const aheadMs = scale > 0 ? Math.max(0, (atMs - this.shown.presentMs) / scale) : 0;
      const sincePerf = typeof performance !== 'undefined' ? performance.now() - this.shown.perfMs : 0;
      when = this.ctx.currentTime + Math.max(0, aheadMs - sincePerf) / 1000;
      this.voice(id, when, opts.gain ?? 1);
    }
    this.log.push({ id, atMs, ctxTime: when });
    if (this.log.length > 200) this.log.shift();
  }

  private voice(id: SoundId, when: number, gainMul: number): void {
    const ctx = this.ctx!;
    const now = ctx.currentTime;
    for (let i = this.active.length - 1; i >= 0; i--) if (this.active[i].end < now) this.active.splice(i, 1);
    if (this.active.length >= MAX_VOICES || this.active.filter((a) => a.id === id).length >= MAX_SAME_ID) return;
    const n = (this.repeats.get(id) ?? 0) + 1;
    this.repeats.set(id, n);
    const pitch = n > 1 ? 1 + ((n * 7919) % 9 - 4) / 100 : 1;   // deterministic ±4 % on repeats
    const out = ctx.createGain();
    out.gain.value = gainMul;
    out.connect(this.buses![BUS_OF[id]]);
    const dur = recipe(ctx, id, when, pitch, out);
    this.active.push({ id, end: when + dur });
  }
}

/** ui-ux §8.3 placeholder recipes. Returns the duration in seconds. */
function recipe(ctx: AudioContext, id: SoundId, t: number, pitch: number, out: AudioNode): number {
  const tone = (type: OscillatorType, f: number, dur: number, g: number, at = t, attack = 0.002): void => {
    const o = ctx.createOscillator(); const e = ctx.createGain();
    o.type = type; o.frequency.value = f * pitch;
    e.gain.setValueAtTime(0.0001, at); e.gain.exponentialRampToValueAtTime(g, at + attack); e.gain.exponentialRampToValueAtTime(0.0001, at + dur);
    o.connect(e).connect(out); o.start(at); o.stop(at + dur + 0.02);
  };
  const noise = (dur: number, g: number, filter: BiquadFilterType, f0: number, f1 = f0, at = t): void => {
    const len = Math.max(1, Math.floor(ctx.sampleRate * dur));
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const ch = buf.getChannelData(0);
    let s = 22222;                                            // deterministic noise (no Math.random in the sound path)
    for (let i = 0; i < len; i++) { s = (s * 16807) % 2147483647; ch[i] = (s / 1073741823.5 - 1) * (1 - i / len); }
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bq = ctx.createBiquadFilter(); bq.type = filter; bq.frequency.setValueAtTime(f0, at); bq.frequency.linearRampToValueAtTime(f1, at + dur);
    const e = ctx.createGain(); e.gain.value = g;
    src.connect(bq).connect(e).connect(out); src.start(at);
  };
  switch (id) {
    case 'ui_tap': tone('triangle', 2400, 0.018, 0.15); return 0.03;
    case 'ui_select': tone('triangle', 1800, 0.014, 0.1); return 0.03;
    case 'ui_page': noise(0.18, 0.12, 'bandpass', 1200, 600); return 0.2;
    case 'ui_sheet': noise(0.12, 0.08, 'lowpass', 2000); return 0.14;
    case 'ui_deny': tone('sine', 140, 0.09, 0.3); return 0.1;
    case 'ui_seal': tone('sine', 90, 0.06, 0.5); noise(0.008, 0.2, 'highpass', 3000); return 0.08;
    case 'coin': tone('sine', 2600, 0.12, 0.12); tone('sine', 3900, 0.12, 0.08); return 0.13;
    case 'xp_tick': tone('triangle', 750, 0.08, 0.1); return 0.1;
    case 'levelup': tone('sine', 523, 0.6, 0.15, t, 0.08); tone('sine', 659, 0.6, 0.12, t + 0.08, 0.08); tone('sine', 784, 0.6, 0.12, t + 0.16, 0.08); return 0.8;
    case 'hit_light': noise(0.07, 0.3, 'lowpass', 1800); tone('sine', 160, 0.07, 0.3); return 0.08;
    case 'hit_heavy': noise(0.12, 0.4, 'lowpass', 1200); tone('sine', 90, 0.12, 0.45); return 0.13;
    case 'crit': noise(0.12, 0.4, 'lowpass', 1200); tone('sine', 90, 0.12, 0.45); tone('sine', 1200, 1.5, 0.12); return 1.5;
    case 'ko': tone('sine', 110, 1.6, 0.35); tone('sine', 220, 1.2, 0.12); tone('sine', 330, 0.9, 0.06); return 1.6;
    case 'omen_open': tone('sine', 880, 0.3, 0.1); tone('triangle', 400, 0.8, 0.08, t, 0.7); return 0.8;
    case 'windup': noise(0.3, 0.15, 'bandpass', 400, 1600); return 0.3;
    case 'prayer': tone('sine', 440, 0.4, 0.12, t, 0.05); tone('sine', 880, 0.4, 0.04, t + 0.05, 0.05); return 0.45;
    case 'card_draw': noise(0.06, 0.15, 'highpass', 2500); return 0.06;
    case 'card_place': noise(0.08, 0.25, 'lowpass', 900); return 0.08;
    case 'quest_done': tone('sawtooth', 220, 0.4, 0.08, t, 0.03); return 0.4;
    case 'notify': tone('sine', 1600, 0.25, 0.08); return 0.25;
    case 'error': tone('sine', 140, 0.09, 0.3); tone('sine', 140, 0.09, 0.3, t + 0.12); return 0.22;
  }
}
